import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from './Dashboard';
import { API } from '../App';
import { toast } from 'sonner';
import { Building2, TrendingUp, TrendingDown, DollarSign, Globe, Users } from 'lucide-react';

const GlobalDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGlobalStats();
  }, []);

  const loadGlobalStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/global-stats`);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load global statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading global statistics...</div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="text-center py-12 text-gray-500">No data available</div>
      </Layout>
    );
  }

  const { establishments, global: g } = data;

  return (
    <Layout>
      <div className="space-y-8" data-testid="global-dashboard">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Global Dashboard</h2>
          <p className="text-gray-500 mt-1">Overview of all establishments and activities</p>
        </div>

        {/* Global Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-500">Total Revenue</h3>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{g.total_gain_dt.toFixed(2)} DT</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {g.gain_breakdown.eur > 0 && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{g.gain_breakdown.eur.toFixed(2)} EUR</span>}
              {g.gain_breakdown.usd > 0 && <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">{g.gain_breakdown.usd.toFixed(2)} USD</span>}
              {g.gain_breakdown.dt > 0 && <span className="text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded">{g.gain_breakdown.dt.toFixed(2)} DT</span>}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-500">Total Expenses</h3>
              <TrendingDown className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-600">{g.total_expenses_dt.toFixed(2)} DT</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-500">Net Balance</h3>
              <DollarSign className="w-5 h-5 text-blue-500" />
            </div>
            <p className={`text-2xl font-bold ${g.net_gain_dt >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {g.net_gain_dt.toFixed(2)} DT
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-500">Total Guests</h3>
              <Users className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{g.total_guests}</p>
            <p className="text-xs text-gray-400 mt-1">{g.total_reservations} reservations</p>
          </div>
        </div>

        {/* Per-Establishment Stats */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Building2 className="w-5 h-5 mr-2" />
            By Establishment
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {establishments.map((est) => (
              <div key={est.id || 'unassigned'} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">{est.name}</h4>
                    <span className="text-xs text-gray-400">{est.type}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-500">{est.total_reservations} reservations</span>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-600 font-medium">Revenue</p>
                    <p className="text-lg font-bold text-green-800">{est.total_gain_dt.toFixed(2)}</p>
                    <p className="text-xs text-green-500">DT</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-xs text-red-600 font-medium">Expenses</p>
                    <p className="text-lg font-bold text-red-800">{est.total_expenses_dt.toFixed(2)}</p>
                    <p className="text-xs text-red-500">DT</p>
                  </div>
                  <div className={`${est.net_gain_dt >= 0 ? 'bg-blue-50' : 'bg-orange-50'} rounded-lg p-3`}>
                    <p className={`text-xs ${est.net_gain_dt >= 0 ? 'text-blue-600' : 'text-orange-600'} font-medium`}>Net</p>
                    <p className={`text-lg font-bold ${est.net_gain_dt >= 0 ? 'text-blue-800' : 'text-orange-800'}`}>{est.net_gain_dt.toFixed(2)}</p>
                    <p className={`text-xs ${est.net_gain_dt >= 0 ? 'text-blue-500' : 'text-orange-500'}`}>DT</p>
                  </div>
                </div>
                
                {(est.gain_breakdown.eur > 0 || est.gain_breakdown.usd > 0 || est.gain_breakdown.dt > 0) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {est.gain_breakdown.eur > 0 && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{est.gain_breakdown.eur.toFixed(2)} EUR</span>}
                    {est.gain_breakdown.usd > 0 && <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">{est.gain_breakdown.usd.toFixed(2)} USD</span>}
                    {est.gain_breakdown.dt > 0 && <span className="text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded">{est.gain_breakdown.dt.toFixed(2)} DT</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Nationalities */}
        {g.nationalities.length > 0 && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Globe className="w-5 h-5 mr-2" />
              Guest Nationalities ({g.nationalities.length} countries)
            </h3>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {g.nationalities.map((n) => (
                  <div key={n.country} className="flex justify-between items-center bg-gray-50 rounded-lg px-4 py-2">
                    <span className="text-sm text-gray-700">{n.country}</span>
                    <span className="text-sm font-bold text-gray-900">{n.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default GlobalDashboard;
